/**
 * Copyright 2026 Salesforce, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import { type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import type { ShopperSearch } from '@/scapi';
import { CarouselItem } from '@/components/ui/carousel';
import { ProductTile, ProductTileProvider } from '@/components/product-tile';
import type { TileSurface } from '@/lib/brand/overlay-model';
import DynamicImageProvider from '@/providers/dynamic-image';
import withSuspense from '@/components/with-suspense';
import ProductCarouselSkeleton from './skeleton';
import { cn } from '@/lib/utils';
import type { ComponentType } from '@/components/region';
import { Component } from '@/components/region/component';
import { usePageDesignerMode } from '@salesforce/storefront-next-runtime/design/react/core';
import { CarouselSection } from '@/components/carousel-section';

const carouselItemImageWidths = ['348px', '256px', '256px', '288px'];

/* SLIDE WIDTH IS A TOKEN WITH A FALLBACK, AND THE FALLBACK IS THE OLD VALUE.
   The three responsive width arms on the CarouselItem below each read
   `--rail-slide-width` with a per-breakpoint fallback restating `348 / 256 /
   288` — the exact widths this file shipped before — so a carousel whose
   ancestors never declare that variable computes byte-identically to what it
   computed before. Only a surface that opts in moves.

   DO NOT WRITE THE ARM'S CLASS SYNTAX INTO THIS COMMENT. An earlier draft
   spelled one out with a placeholder in the fallback slot; Tailwind's scanner
   treats any candidate string in a scanned source file as a usage and emitted
   a real rule from it, placeholder and all, into the production stylesheet.
   Describe the arms, never quote them.
   That is the point: this component mounts on the homepage rail, the PDP
   recommendation rails, the cart page and the mini-cart, and only the PDP rails
   have a measured per-brand ladder. A default declared at `:root` would have
   inherited into all four.

   WHY A COUNT AND NOT A PIXEL. Live drives both brands' rails from a slides-
   per-view number, not a width — `pdp-wireframe-spec.md` §2.2 and §2.3
   reconcile the two engines' own formulas against the captures at six widths
   each, exactly:

     NM (swiper)  slide = (track - gap x (n-1)) / n
                  n = 2.5 (<=440) | 4.5 (768) | 5 (>=1024)
     BG (slick)   slide = ceil(track / n)
                  n = 2.5 (<=440) | 4.5 (768) | 6.5 (1024) | 8 (>=1280)

   A COUNT TRANSFERS ACROSS CONTAINER WIDTHS AND A PIXEL DOES NOT, which is the
   whole reason the old fixed widths diverged: our rail viewport is 1152 CSS at
   1280 against live NM's 1180 and live BG's 1124, so no single hardcoded width
   could be right on both brands even once.

   THE CALC LIVES IN THE BRAND OVERLAYS, scoped to `.pdp-rail`, because the
   count is a per-brand measured value and the overlays are where those live —
   `theme/tokens/brand-{nm,bg}/pdp.css`. They declare
   `--rail-slide-width: calc(100% / <n>)`.

   `100%` IS THE TRACK, AND THE TRACK IS DELIBERATELY WIDER THAN THE VIEWPORT.
   `CarouselSection` gives the flex track `-ml-4` and every item `pl-4`, so the
   track measures viewport + 16px and each item's ink starts one gap in. That is
   the same geometry swiper's `spaceBetween` produces: with pitch W and gap g,
   n slides of ink plus (n-1) gaps span nW - g, so n slides fit the viewport V
   exactly when W = (V + g)/n — and (V + g) IS the track. So `calc(100% / n)`
   over this track is live's formula, not an approximation of it. Measured
   back: BG at 1280 goes 4.5-up -> 8-up with the ink at 130px, inside live's own
   125-152px slide range for that brand.

   NOT LANDED HERE, and it is a real remainder: NM's gap steps 12px (<=768) ->
   45px (>=1024) where ours is a flat 16px. The gap is one value in two places —
   `pl-4` on the item and the compensating `-ml-4` on the track — and the track
   half is set by `components/carousel-section`, which this change does not own.
   Moving only the item half would indent the first tile by the difference. */

/* ART-BOX SHAPE IS NOT DECLARED HERE, DELIBERATELY, and the absence is the fix.
   This file used to hold `const productCarouselItemAspectRatio = 0.8` and pass
   it to every `ProductTile` as `imgAspectRatio`. That prop is a PER-INSTANCE
   override — `product-image/index.tsx` honours it by writing an inline
   `aspect-ratio` and dropping the `aspect-[var(--tile-art-aspect)]` class
   entirely — so passing it from here set a brand-blind default for a whole
   class of surfaces (home rail, PDP and cart recommendation rails) rather than
   overriding one tile.

   0.8 is NM's number. `brand-nm/tile.css` declares `--tile-art-aspect: 380 / 475`,
   which is 0.8 exactly, so on NM the two agree to the pixel; on BG, whose token is
   `5 / 7` (140%), the inline 0.8 painted NM's 125% on BG's 5:7 photography.

   Passing nothing lets `ProductTile` fall through to
   `config.global.productListing.defaultProductTileImgAspectRatio` (1), which is
   the value that KEEPS the token class — so a carousel tile now resolves the same
   per-brand token the search and category grids resolve. It also makes the two
   branches below agree: the Page-Designer region branch renders `Content.productTile`
   components that never carried this prop, so a data-driven rail and an authored
   rail were painting different shapes in the same component. */

export interface ProductCarouselProps {
    /** Array of product search hits to display in the carousel */
    products: ShopperSearch.schemas['ProductSearchHit'][];
    /** Optional title to display above the carousel */
    title?: string;
    /** Optional subtitle displayed below the title */
    subtitle?: string;
    /** Optional "Shop all" link URL displayed next to the title */
    shopAllUrl?: string;
    /** Optional label for the "Shop all" link. Defaults to "Shop all" */
    shopAllText?: string;
    /** Optional className for the title heading. Defaults to text-2xl md:text-3xl font-normal text-foreground tracking-tight */
    titleClassName?: string;
    /** Optional className to apply to the carousel wrapper */
    className?: string;
    /** Optional Page Designer component for container rendering mode */
    component?: ComponentType;
    /**
     * WHERE this carousel sits. Decides whether NM's tiles open the product
     * drawer or navigate — see lib/brand/overlay-model.ts.
     *
     * Defaults to `recommendation`, which navigates. A carousel is NOT a
     * product grid, but it is also not automatically a homepage rail: live NM
     * opens the drawer from the homepage and navigates from search results, and
     * PDP/cart recommendation rails are unmeasured. The homepage opts in
     * explicitly; everything else stays on the safe default.
     */
    surface?: TileSurface;
}

/**
 * ProductCarousel component displays a horizontal carousel of product tiles.
 *
 * This component renders a responsive carousel with navigation controls that displays
 * a collection of products in a scrollable horizontal layout. It's commonly used
 * for featured products, recommendations, or product collections on home and category pages.
 *
 * @param props - The component props
 * @param props.products - Array of product search hits to display in the carousel
 * @param props.title - Optional title to display above the carousel
 * @param props.className - Optional className to apply to the carousel wrapper
 *
 * @returns JSX element representing the product carousel, or a translated "No products found" message
 *
 * @example
 * ```tsx
 * // Basic usage with products array
 * <ProductCarousel
 *   products={searchResult.hits}
 *   title="Featured Products"
 * />
 *
 * // Usage without title
 * <ProductCarousel products={products} />
 *
 * // Usage with custom className
 * <ProductCarousel products={products} className="mt-8" />
 * ```
 *
 * @since 1.0.0
 */
export default function ProductCarousel({
    products,
    title,
    subtitle,
    shopAllUrl,
    shopAllText,
    titleClassName,
    className,
    component,
    surface = 'recommendation',
}: ProductCarouselProps): ReactNode {
    const { t } = useTranslation('product');
    const { isDesignMode } = usePageDesignerMode();
    const productsRegion = component?.regions?.find((region) => region.id === 'products');
    const regionComponents = productsRegion?.components ?? [];
    const resolvedTitle = title || ''; // put empty string as the title since dont currently have i18n for these default values.

    /* THE HOMEPAGE RAIL'S HEADING IS A CENTRED BLOCK, and only that surface's is.
       Read off the PAINTING element — the `<h5>` that carries the text node, not
       an ancestor — in all eight captured NM homepage widths. Both emissions of
       each capture are byte-identical, so the noise floor on this read is zero:

         .reference/captures/home-m-{375,390,440}/App.tsx
           <h5 className="text-center mt-[30px] ml-[30px] mr-[30px]
                          text-[32px] leading-[48px]"
               style={{fontFamily: 'romie, "Times New Roman", …, serif'}}>
         .reference/captures/home-d-{768,1024,1280,1440,1920}/App.tsx
           the same element at text-[40px] leading-[60px]

       Its only sibling is an EMPTY `<p>`, and the wrapper around the pair is a
       bare `text-center` div — live has no shop-all control in this row, which
       is why dropping the link (already done at the route) and centring the
       heading are the same finding arriving in two halves.

       KEYED ON THE SURFACE, NOT THE BRAND. `home-rail` is mounted by exactly one
       thing — a `product-rail` section in the homepage content document — and
       only NM's document places one (`adapters/home-content/seed/*.seed.json`:
       NM has `product-rail-new-picks`, BG's list is banners, a shelf and two
       editorial grids). So this makes no claim about BG, and it leaves every
       other surface on the left row it already had; the PDP and cart
       recommendation rails were not measured in this pass and must not move.

       THE UPPERCASE IS IN THE CONTENT, NOT IN CSS. Live's `<h5>` carries no
       `uppercase` class (its sibling `<p>` does); the copy arrives uppercase
       from the CMS field itself — the page's own Contentful blob records
       `"contentType":"stylyzeProductCarousel" … "title":"NEW PICKS FOR YOU"`.
       Do not add a text-transform here.

       TYPE IS NOT SET HERE. Family, size and leading are brand values and land
       in theme/tokens/brand-nm/home.css; this decides STRUCTURE only, which is
       what no CSS value can express. */
    const titleAlign = surface === 'home-rail' ? 'center' : 'left';

    // When there are no products and no region components, only show the prompt in
    // Page Designer design mode (to guide the content author). On the live storefront
    // render nothing to avoid showing a confusing placeholder to shoppers.
    if ((!products || products.length === 0) && regionComponents.length === 0) {
        if (!isDesignMode) {
            return null;
        }
        return (
            <div className={cn('section-container py-6', className)}>
                <div role="status" aria-live="polite">
                    {t('selectProduct')}
                </div>
            </div>
        );
    }

    return (
        <CarouselSection
            title={resolvedTitle}
            subtitle={subtitle}
            shopAllUrl={shopAllUrl}
            shopAllText={shopAllText}
            titleClassName={titleClassName}
            titleAlign={titleAlign}
            className={className}
            ariaLabel={`${resolvedTitle} carousel`}>
            {regionComponents.length > 0 && productsRegion ? (
                regionComponents.map((comp) => {
                    const typedComp = comp as ComponentType;
                    const key = typedComp.contentLinkUuid ?? typedComp.id;
                    return (
                        <CarouselItem
                            key={key}
                            className="w-[var(--rail-slide-width,348px)] md:w-[var(--rail-slide-width,256px)] 2xl:w-[var(--rail-slide-width,288px)] basis-auto py-1 flex">
                            <div className="w-full max-w-full min-w-0 flex">
                                <Component
                                    component={typedComp}
                                    regionId={productsRegion.id}
                                    className="h-full w-full"
                                />
                            </div>
                        </CarouselItem>
                    );
                })
            ) : (
                <ProductTileProvider surface={surface}>
                    <DynamicImageProvider value={{ widths: carouselItemImageWidths }}>
                        {products.map((product) => (
                            <CarouselItem
                                key={product.productId}
                                className="w-[var(--rail-slide-width,348px)] md:w-[var(--rail-slide-width,256px)] 2xl:w-[var(--rail-slide-width,288px)] basis-auto py-1 flex">
                                <div className="w-full max-w-full min-w-0 flex">
                                    {/* NO SWATCH ROW ON A RAIL TILE, ON EITHER BRAND. Measured on
                                        the RAW DUMPS rather than the decompiled captures, and that
                                        choice is the whole point: the capture decompiler strips
                                        live's own class names, so a swatch is invisible there BY
                                        CONSTRUCTION and its absence is a fact about the search.
                                        The dumps preserve live's semantic classes and emit each
                                        page ONCE (`<html>` = 1 in each), so no halving applies.

                                        Every count below comes from walking the rail's CONTAINER to
                                        its matching close and enumerating children — not from
                                        counting matches and stopping when the list looked complete.

                                        home-rail, live NM (.reference/homepage.mobile.html)
                                          container  div.stylyze-carousel, 11,922 bytes, titled
                                                     <h5>NEW PICKS FOR YOU</h5>
                                          members    14 tiles, role="group" aria-label="0 / 14"
                                                     through "13 / 14" — the list is BOUNDED, first
                                                     child at region offset 428, last at 11,198
                                          anatomy    every one of the 14 has exactly FOUR children:
                                                     a.cart-item-tile__link (holding the img),
                                                     div.cart-item-tile__designer,
                                                     div.cart-item-tile__product,
                                                     div.cart-item-tile__price
                                          swatches   0 in the region
                                        The desktop dump cannot corroborate and is not cited as if it
                                        could: its stylyze carousel rendered five
                                        `carousel-section__skeleton` slides, so it holds the rail's
                                        shell and none of its tiles.

                                        recommendation, live NM (.reference/cart.desktop.html)
                                          container  the YOU MAY ALSO LIKE carousel, 23,830 bytes
                                          members    23 tiles, aria-label "1 / 23" … "23 / 23"
                                          swatches   0, against a positive control of 92
                                                     `ProductTile__` occurrences in the SAME region

                                        recommendation, live BG (.reference/bg-pdp-regular.desktop.html)
                                          container  div.stylyze-carousel, 23,926 bytes, titled
                                                     <h2>You may also like</h2>
                                          members    9 tiles, one child each
                                                     (a.stylyze-carousel__product-link)
                                          swatches   0 in the region. The file's seven `swatch`
                                                     strings are all the PDP's OWN colour picker
                                                     (li.product-options__list__swatch-item) and its
                                                     hydration JSON — outside this container.

                                        THE POSITIVE CONTROL THAT MAKES THOSE ZEROES MEAN SOMETHING:
                                        .reference/plp-womens-clothing.desktop.html carries
                                        `product-thumbnail__swatches` 120 times, one per tile, plus
                                        ~370 further swatch-named classes. The dump tier plainly CAN
                                        see a swatch row where live paints one. The NM homepage dumps
                                        carry three `SWATCH` strings between them and all three are
                                        feature-flag names in a JSON config blob
                                        (SWATCH_OPTIMIZATION, PDP_CIRCULAR_SWATCHES,
                                        PLP_IMAGE_SWATCH_LAZY_LOAD_BETA) — zero markup.

                                        SCOPED TO THE CAROUSEL DELIBERATELY. Live's PLP and search
                                        tiles DO carry the row and it stays; this component is the
                                        only mount for the two surfaces measured without it. Suppressed
                                        by the tile's own documented cap rather than by hiding a
                                        rendered row, so the block, its lazy chunk and its skeleton
                                        are never mounted at all. */}
                                    <ProductTile product={product} maxSwatches={0} className="h-full w-full" />
                                </div>
                            </CarouselItem>
                        ))}
                    </DynamicImageProvider>
                </ProductTileProvider>
            )}
        </CarouselSection>
    );
}

/**
 * ProductCarouselWithSuspense component provides a ProductCarousel wrapped with a Suspense boundary.
 *
 * This component automatically shows the ProductCarouselSkeleton as a fallback while the
 * ProductCarousel is loading, providing better user experience during data fetching.
 *
 * When used with a `resolve` prop, the resolved data should be a ProductSearchResult
 * that will be passed as the `products` prop to the ProductCarousel component.
 *
 * @example
 * ```tsx
 * // Basic usage with Suspense boundary
 * <ProductCarouselWithSuspense
 *   products={searchResult.hits}
 *   title="Featured Products"
 * />
 *
 * // Usage with promise resolution as a prop
 * <ProductCarouselWithSuspense
 *   resolve={searchResultPromise}
 *   title="Featured Products"
 * />
 *
 * // Usage in a page with streaming
 * function HomePage() {
 *   return (
 *     <div>
 *       <Hero />
 *       <ProductCarouselWithSuspense resolve={searchResultPromise} title="Shop Products" />
 *     </div>
 *   );
 * }
 * ```
 */
export const ProductCarouselWithSuspense = withSuspense(ProductCarouselWithData, {
    fallback: (props) => <ProductCarouselSkeleton {...props} />,
});

/**
 * Internal component that handles data transformation for ProductCarousel.
 * This component receives the resolved data and transforms it to the expected format.
 * Only supports ProductSearchResult and ProductSearchHit types for simplicity.
 */
export function ProductCarouselWithData({
    data,
    title,
    ...props
}: {
    data?: ShopperSearch.schemas['ProductSearchResult'] | ShopperSearch.schemas['ProductSearchHit'][];
    title?: string;
    [key: string]: unknown;
}) {
    // If data is provided (from resolve), extract products from it
    if (data) {
        // Handle ProductSearchResult (has hits property)
        if ('hits' in data && Array.isArray(data.hits)) {
            return <ProductCarousel products={data.hits} title={title} {...props} />;
        }

        // Handle direct ProductSearchHit array
        if (Array.isArray(data)) {
            return <ProductCarousel products={data} title={title} {...props} />;
        }
    }

    // If no data or unsupported format, render empty state
    return <ProductCarousel products={[]} title={title} {...props} />;
}
